import maintenance from "assets/animations/maintenance.json" with { type: "json" };
import { useLottie } from "lottie-react";
import type { CanvasRendererConfig } from "lottie-web";
import { useEffect } from "react";

// maintenance.json is authored at 740×710.
const ANIMATION_WIDTH = 740;
const ANIMATION_HEIGHT = 710;

// `dpr` is a real lottie-web 5.12.2 canvas runtime option that is missing from CanvasRendererConfig's
// published types. Capping it at 1 rasterizes the looping animation at logical (not 2× retina)
// resolution, which cuts the GPU-process surface ~4× — the whole reason for moving off the default SVG
// renderer. On the long-running iOS WKWebView kiosk that SVG surface re-rasterizes every frame in the
// GPU process (~12–27 MB) and shares its jetsam budget with the decoded dish images. The cast adds the
// untyped field without widening the public type.
const CANVAS_RENDERER_SETTINGS = {
	clearCanvas: true,
	progressiveLoad: false,
	preserveAspectRatio: "xMidYMid meet",
	dpr: 1,
} as CanvasRendererConfig;

/**
 * Looping maintenance animation on a <canvas> instead of lottie's default SVG renderer. It fills its
 * container at the 740×710 aspect ratio, keeping the same on-screen size the SVG had; `dpr: 1` (not a
 * smaller box) is what reclaims the GPU memory. Shared by StartingUpModal and RemoteRefillModal.
 */
export const MaintenanceAnimation = () => {
	const { View, setSubframe } = useLottie<"canvas">(
		{
			animationData: maintenance,
			loop: true,
			autoplay: true,
			renderer: "canvas",
			rendererSettings: CANVAS_RENDERER_SETTINGS,
		},
		{ width: "100%", maxWidth: ANIMATION_WIDTH, aspectRatio: `${ANIMATION_WIDTH} / ${ANIMATION_HEIGHT}` },
	);

	// Whole-frame stepping (no sub-frame interpolation) → fewer canvas redraws per second.
	useEffect(() => {
		setSubframe(false);
	}, [setSubframe]);

	return View;
};
